import useSEO from '@/hooks/useSEO';
import { Link, useLocation } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'Spread Guide', href: '/forex-spread/' },
  { label: 'Commission Guide', href: '/forex-commission/' },
  { label: 'Swap Guide', href: '/forex-swap/' },
  { label: 'Cost Calculator', href: '/calculate-forex-trading-costs/' },
  { label: 'Costs by Strategy', href: '/forex-trading-costs-by-strategy/' },
];

export default function NotFound() {
  const location = useLocation();

  useSEO({
    title: 'Page Not Found: FXCostGuide',
    description: 'The requested page could not be found. Explore FXCostGuide resources on spread, commission, swap, slippage and total trading costs.',
  });


  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <div className="flex-1 bg-foreground-950 flex items-center justify-center px-4 py-16 relative overflow-hidden">

        {/* Background dots */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--primary-500)) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Decorative circles */}
        <div className="absolute top-1/3 left-[15%] w-[220px] h-[220px] rounded-full border border-primary-500/6" />
        <div className="absolute top-1/3 left-[17%] w-[150px] h-[150px] rounded-full border border-primary-500/4" />
        <div className="absolute bottom-1/4 right-[12%] w-[200px] h-[200px] rounded-full border border-secondary-500/5" />
        <div className="absolute bottom-1/4 right-[14%] w-[130px] h-[130px] rounded-full border border-secondary-500/3" />

        {/* Subtle glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[350px] rounded-full opacity-[0.04] blur-[140px] bg-primary-500" />

        {/* Content */}
        <div className="relative z-10 text-center max-w-[560px]">

          {/* 404 number */}
          <p className="text-[100px] md:text-[140px] font-[850] leading-none tracking-[-0.04em] text-foreground-800 select-none mb-2">
            404
          </p>

          {/* Heading */}
          <h1 className="text-[22px] md:text-[28px] font-[750] text-background-50 leading-tight mb-3">
            Off the Charts &mdash; Page Not Found
          </h1>

          {/* Path display */}
          <p className="text-[13px] font-mono text-foreground-600 bg-foreground-900/60 inline-block px-3 py-1.5 rounded-md mb-4">
            {location.pathname}
          </p>

          {/* Description */}
          <p className="text-[15px] leading-relaxed text-foreground-400 mb-8 max-w-[440px] mx-auto">
            The page you&apos;re looking for doesn&apos;t exist or has been moved. Unlike a bad trade though, this one&apos;s easy to recover from — pick a path below and keep going.
          </p>

          {/* Primary CTA */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-btn text-[15px] font-semibold bg-primary-500 text-background-50 no-underline hover:bg-primary-600 transition-colors whitespace-nowrap mb-6"
          >
            <i className="ri-home-4-line text-base"></i>
            Back to Home
          </Link>

          {/* Quick links */}
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {quickLinks.filter((l) => l.href !== '/').map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="px-4 py-2 bg-foreground-800 text-foreground-300 rounded-btn text-[14px] font-medium no-underline hover:bg-foreground-700 hover:text-foreground-100 transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}